/**
 * System Settings (Legacy) — Domain API Module
 *
 * Wraps the shared V2 API client for all data operations on the
 * LegacySystemSettings page (CampusConfig, Devices, ReasonCodes, Server).
 */

import {
  apiDynamoGetItem,
  apiDynamoGetAll,
  apiDynamoSave,
  apiRedQuery,
  apiQueryItems,
  apiFetch,
  apiTransaction,
} from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/** Fetch the CampusConfig DynamoDB item for a given campus GUID. */
export function fetchCampusConfig(campusGuid) {
  return apiDynamoGetItem({
    table: 'CampusConfig',
    partitionKey: 'CampusId',
    partitionValue: campusGuid,
    campusId: campusGuid,
  });
}

/** Fetch distinct device names from the Redis-cached devices table. */
export async function fetchDeviceNames(campusGuid) {
  const data = await apiRedQuery({
    table: 'devices',
    column: 'devicename',
    getType: 'distinct',
    sortColumn: 'devicename',
    sortOrder: 'ASC',
    campusId: campusGuid,
  });
  // V1 maps rows to flat string array: data[i].devicename
  return (data || []).map((row) => row.devicename).filter(Boolean);
}

/** Fetch campus-specific reason codes (non-deleted). */
export async function fetchReasonCodes(campusGuid) {
  const data = await apiQueryItems({
    table: 'CampusReasonCode',
    partitionKey: 'CampusId',
    partitionValue: campusGuid,
    campusId: campusGuid,
    includeDeleted: true,
  });
  return {
    active: (data || []).filter(
      (item) => [null, undefined].includes(item?.Deleted) || item?.Deleted === false
    ),
    all: data || [],
  };
}

/**
 * This campus's own live core_reason_codes rows (the store reports label from). The procedure
 * also returns corporate-shared rows, which a non-cloud campus's app never offers, so those are
 * dropped. Resolves to [] on error.
 */
export async function fetchMysqlReasonCodes(campusId) {
  const rows = await apiFetch({
    procedure: 'getreasoncodes',
    reqType: 'stored',
    payload: { campus_id: campusId },
  });
  return (Array.isArray(rows) ? rows : []).filter((r) => String(r.campus_id) === String(campusId));
}

/** Fetch the global ReasonCode reference table. */
export function fetchGlobalReasonCodes(campusGuid) {
  return apiDynamoGetAll({
    table: 'ReasonCode',
    campusId: campusGuid,
  });
}

/** Fetch server system config from the stored procedure. */
export function fetchServerConfig(campusId) {
  return apiFetch({
    procedure: 'server_system_config',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Save fields to the CampusConfig DynamoDB item.
 * @param {Array} payload — Array with a single config object
 * @param {string} campusGuid
 */
export function saveCampusConfig(payload, campusGuid) {
  return apiDynamoSave({
    table: 'CampusConfig',
    partitionKey: 'CampusId',
    payload,
    campusId: campusGuid,
  });
}

/** Edit existing reason codes (description / category changes). */
export function editReasonCodes(payload, { campusId, userId, clientId }) {
  return apiTransaction({
    transType: 'editlegacyreasoncode',
    payload,
    oldPayload: [],
    campusId,
    userId,
    clientId,
  });
}

/** Add one or more reason codes (single add or bulk create). The Lambda assigns each row's
 *  ReasonCodeId (never reusing a deleted one), so rows are sent without one. */
export function addReasonCodes(payload, { campusId, userId, clientId }) {
  return apiTransaction({
    transType: 'addlegacyreasoncode',
    payload,
    oldPayload: [],
    campusId,
    userId,
    clientId,
  });
}

/** Soft-delete a reason code (sets Deleted=true with TTL). */
export function deleteReasonCode(payload, { campusId, userId, clientId }) {
  return apiTransaction({
    transType: 'deletelegacyreasoncode',
    payload,
    oldPayload: [],
    campusId,
    userId,
    clientId,
  });
}

/** Save server-level settings (e.g. notification email). */
export function saveServerSettings(payload, { campusId, userId, clientId }) {
  return apiTransaction({
    transType: 'system_server_settings',
    payload,
    campusId,
    userId,
    clientId,
  });
}
